import React from 'react';

import InputForm from '../../../../components/form/InputForm';
import SelectForm from '../../../../components/form/SelectForm';
import TableFilterInput from '../../../../components/table/TableFilterInput';
import Select from '../../../../components/input/Select/Select';
import Button from '../../../../components/Button';
import { setDateFormat } from '../../../../lib/moment';
import { DATE_FORMAT } from '../../../../constants/service/common/utilConstants';
import {
  CATEGORY_EXHIBIT_TYPE,
  CATEGORY_EXHIBIT_TYPE_SELECT_LIST,
  CATEGORY_EXHIBIT_TYPE_TEXT,
  CATEGORY_STATUS_TYPE_SELECT_LIST,
  CATEGORY_STATUS_TYPE_TEXT,
  SUB_CATEGORY_NAME_MAX_LENGTH,
} from '../../../../constants/service/app/categoryConstants';
import { formRequiredRules } from '../../../../lib/formRules';
import { REX_CATEGORY_NAME } from '../../../../lib/rex';
import {
  INVALID_CATEGORY_NAME_MESSAGE,
  INVALID_REQUIRED_CATEGORY_NAME_MESSAGE,
} from '../../../../constants/service/invalidMsg/categoryInvalidMsg';

/**
 * react-table v6의 normalizeComponent는 Cell이 함수일 때
 * React.createElement(Cell, cellInfo)로 렌더링한다.
 * 매 렌더마다 새로운 함수 참조가 생성되면 React가 다른 컴포넌트 타입으로 인식하여
 * 언마운트/리마운트가 발생한다.
 * form 입력이 포함된 Cell은 모듈 레벨에서 안정적인 참조로 정의하여
 * 리렌더 시에도 동일 컴포넌트로 인식되게 한다.
 */
const NameCell = (row) => {
  const { form, editableRow, disabledAction } = row.column;

  return editableRow === row.index ? (
    <InputForm
      form={form}
      name="name"
      placeholder="하위 카테고리명 입력"
      initialValue={row.original.name}
      disabled={disabledAction}
      rules={[
        formRequiredRules(true, INVALID_REQUIRED_CATEGORY_NAME_MESSAGE),
        { pattern: REX_CATEGORY_NAME, message: INVALID_CATEGORY_NAME_MESSAGE },
      ]}
      maxLength={SUB_CATEGORY_NAME_MAX_LENGTH}
    />
  ) : (
    row.original.name
  );
};

const StatusCell = (row) => {
  const { form, editableRow, disabledAction } = row.column;

  return editableRow === row.index ? (
    <SelectForm
      isEditable
      name="status"
      form={form}
      initialValue={row.original.status}
      list={CATEGORY_STATUS_TYPE_SELECT_LIST}
      disabled={disabledAction}
      isTable
    />
  ) : (
    CATEGORY_STATUS_TYPE_TEXT[row.original.status]
  );
};

const columns = ({
  form,
  editableRow,
  onEditRow,
  onRemoveRow,
  onCancelRow,
  onCreateSubCategory,
  onEditSubCategory,
  onDeleteSubCategory,
  disabledAction,
  disabledFilter,
  filterOption,
  onFilter,
}) => [
  {
    width: 150,
    Cell: (row) => (
      <div className="sub-category-btn-wrap">
        {editableRow === row.index ? (
          row.original.id ? (
            // 카테고리 편집
            <>
              <Button color="red" onClick={onCancelRow} text="취소" />
              <Button color="primary" onClick={() => onEditSubCategory(row.original.id)} text="저장" />
            </>
          ) : (
            // 카테고리 생성
            <>
              <Button color="red" onClick={onRemoveRow} text="취소" />
              <Button color="primary" onClick={onCreateSubCategory} text="저장" />
            </>
          )
        ) : (
          <>
            <Button
              color="red"
              disabled={row.original.productCount > 0 || row.original.isExhibit === CATEGORY_EXHIBIT_TYPE.EXHIBIT}
              onClick={() => onDeleteSubCategory(row.original.id)}
              text="삭제"
            />
            <Button
              color="gray"
              onClick={() => {
                onEditRow(row.index, row.original);
              }}
              disabled={disabledAction}
              text="편집"
            />
          </>
        )}
      </div>
    ),
  },
  {
    Header: '카테고리 ID',
    accessor: 'id',
    sortable: !disabledFilter,
    filterable: true,
    width: 100,
    Cell: (row) => row.original.id,
    Filter: () => (
      <TableFilterInput option={filterOption.id} onFilter={(id) => onFilter({ id })} disabled={disabledFilter} />
    ),
  },
  {
    Header: '카테고리 명',
    accessor: 'name',
    sortable: !disabledFilter,
    filterable: true,
    form,
    editableRow,
    disabledAction,
    Cell: NameCell,
    Filter: () => (
      <TableFilterInput option={filterOption.name} onFilter={(name) => onFilter({ name })} disabled={disabledFilter} />
    ),
  },
  {
    Header: '등록 상품 수',
    accessor: 'productCount',
    sortable: !disabledFilter,
    width: 110,
    Cell: (row) => row.original.productCount,
  },
  {
    Header: '활성 여부',
    accessor: 'status',
    sortable: !disabledFilter,
    filterable: true,
    width: 100,
    form,
    editableRow,
    disabledAction,
    Cell: StatusCell,
    Filter: () => (
      <Select
        list={[{ id: null, name: '전체' }, ...CATEGORY_STATUS_TYPE_SELECT_LIST]}
        onChange={(status) => onFilter({ status })}
        defaultValue={filterOption.status}
        value={filterOption.status}
        isTable={true}
        size="large"
        disabled={disabledFilter}
      />
    ),
  },
  {
    Header: '전시 여부',
    accessor: 'isExhibit',
    sortable: !disabledFilter,
    filterable: true,
    width: 100,
    Cell: (row) => {
      if (row.original.isExhibit === CATEGORY_EXHIBIT_TYPE.EXHIBIT)
        return CATEGORY_EXHIBIT_TYPE_TEXT[CATEGORY_EXHIBIT_TYPE.EXHIBIT];
      else if (row.original.isExhibit === CATEGORY_EXHIBIT_TYPE.INEXHIBIT)
        return CATEGORY_EXHIBIT_TYPE_TEXT[CATEGORY_EXHIBIT_TYPE.INEXHIBIT];
      return null;
    },
    Filter: () => (
      <Select
        list={[{ id: null, name: '전체' }, ...CATEGORY_EXHIBIT_TYPE_SELECT_LIST]}
        onChange={(isExhibit) => onFilter({ isExhibit })}
        defaultValue={filterOption.isExhibit}
        value={filterOption.isExhibit}
        isTable={true}
        size="large"
        disabled={disabledFilter}
      />
    ),
  },
  {
    Header: '생성일',
    accessor: 'createDate',
    sortable: !disabledFilter,
    Cell: (row) =>
      row.original.createDate ? setDateFormat(row.original.createDate, DATE_FORMAT.FULL_DASH_FORMAT) : null,
  },
  {
    Header: '수정일',
    accessor: 'updateDate',
    sortable: !disabledFilter,
    Cell: (row) =>
      row.original.updateDate ? setDateFormat(row.original.updateDate, DATE_FORMAT.FULL_DASH_FORMAT) : null,
  },
];

export default columns;
